import React, { useEffect, useMemo, useState } from "react";
import { Checkbox, CheckboxProps, Col, Flex, Row, Select, SelectProps } from "antd";
import { debounce, formatNumberFixed, renderClassNameStatusEsp } from "../../../utils/helper";
import { WorkflowServices } from "../../../services/workflow";
import { SearchOutlined } from "@ant-design/icons";
import { IApWorkflowBscResponse, IBookingSupplierCostMc } from "../../../interfaces/workflow";
interface SearchEspProps {
  selectProps?: SelectProps;
  onChangeEsp: (data: { expectedSupplierPaymentId: string; bookingSupplierCost: IBookingSupplierCostMc }) => void;
  supplierId: string;
  isDisabled?: boolean;
  isTypeFlight: boolean;
  sirSupplierId?: string;
}

const SearchEsp: React.FC<SearchEspProps> = ({
  selectProps,
  onChangeEsp,
  supplierId,
  isDisabled = false,
  isTypeFlight,
  sirSupplierId,
}) => {
  const [fetching, setFetching] = useState(false);
  const [data, setData] = useState<SelectProps["options"]>([]);
  const [debouncedSearchValue, setDebouncedSearchValue] = useState<string | null>("");
  const [textSearch, setTextSearch] = useState<string | null>();
  const [value, setValue] = useState<string | null>();
  const [isIncludeMatched, setIsIncludeMatched] = useState<boolean>(false);

  const fetchData = async () => {
    try {
      setFetching(true);
      const params = {
        keyword: debouncedSearchValue || undefined,
        includeMatched: isIncludeMatched ? 1 : 0,
        supplierId: supplierId,
        type: isTypeFlight ? "Flight" : "Common",
      };
      const response: any = await WorkflowServices.getSearchEsp(params);
      const { success = false, data = [] } = response || {};
      if (success) {
        setData(data);
      }
    } catch (error) {
      console.error("Fetch suppliers error:", error);
    } finally {
      setFetching(false);
    }
  };

  useEffect(() => {
    if (debouncedSearchValue) {
      fetchData();
    } else setData([]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debouncedSearchValue, isIncludeMatched]);

  const handleSearch = (newValue: string) => {
    setFetching(true);
    setTextSearch(newValue);
    debouncedSearch(newValue);
  };

  const debouncedSearch = useMemo(
    () =>
      debounce((value: string) => {
        setDebouncedSearchValue(value);
      }, 300),
    []
  );

  const handleChange = async (newValue: string) => {
    const findEsp = data?.find((d) => d.id === newValue);
    setValue(null);
    setTextSearch(null);
    setDebouncedSearchValue(null);
    try {
      const params = {
        bookingRef: findEsp?.bookingRef,
        supplierCode: findEsp?.supplierCode,
        sirSupplierId,
      };
      const response: IApWorkflowBscResponse = await WorkflowServices.getApWorkflowBsc(params);
      const { success = false, data } = response || {};
      if (success) {
        onChangeEsp({
          expectedSupplierPaymentId: findEsp?.id!,
          bookingSupplierCost: data,
        });
      }
    } catch (error) {
      console.error("Fetch Data error:", error);
    }
  };

  const onChangeCheckbox: CheckboxProps["onChange"] = (e) => {
    setIsIncludeMatched(e.target.checked);
  };

  const optionRender = (option: any) => {
    return (
      <Row align={"middle"} gutter={12}>
        <Col span={4} className={renderClassNameStatusEsp(option.data.status)}>
          {option.data.status}
        </Col>
        <Col span={4}>{option.data.supplierCode}</Col>
        <Col span={4}>{option.data.bookingRef}</Col>
        <Col span={4}>{option.data.leadPaxName}</Col>
        <Col span={4} style={{ textAlign: "right" }}>
          {formatNumberFixed(option.data.localCurrencyCost)}
        </Col>
        <Col span={4}>{option.data.type}</Col>
      </Row>
    );
  };

  return (
    <Flex align="center" gap={16} justify="space-between" style={{ width: "100%" }}>
      <Select
        {...selectProps}
        value={value}
        placeholder={"Enter Lead Pax Name or Booking Ref ID"}
        showSearch
        defaultActiveFirstOption={false}
        prefix={<SearchOutlined />}
        suffixIcon={!fetching ? null : undefined}
        filterOption={false}
        onSearch={handleSearch}
        onChange={handleChange}
        notFoundContent={textSearch && !fetching && data?.length === 0 ? "No Data" : null}
        options={(data || []).map((d) => ({
          ...d,
          value: d.id,
          label: d.bookingRef + " " + d.leadPaxName,
        }))}
        optionRender={optionRender}
        className="w-full"
        loading={fetching}
        onBlur={() => {
          setFetching(false);
          setDebouncedSearchValue(null);
          setTextSearch(null);
          setData([]);
        }}
        popupMatchSelectWidth={610}
        placement="bottomLeft"
        disabled={isDisabled}
      />
      <Checkbox onChange={onChangeCheckbox} style={{ whiteSpace: "nowrap" }} disabled={isDisabled}>
        Include Matched
      </Checkbox>
    </Flex>
  );
};

export default SearchEsp;
